import { Page, R } from '@/api/common-types'
import lcRequest from '@/api/request'
import { SysMenuQueryDTO, CreateMenuDTO, UpdateMenuDTO, CheckMenuNameUniqueDTO } from '@/api/dto/menu-dto'
import { SysMenuVO, MenuTreeVO } from '@/api/vo/menu-vo'
import { RouterVo } from '@/api/vo/user-vo'
import { RoleMenuTreeDataVO } from '@/api/vo/role-vo'

const MODULE_MENU_URL = '/admin/menu'

/**
 * 查询菜单分页列表
 * @description 用于分页查询菜单列表
 * @param queryInfo 查询条件
 * @returns Promise<R<Page<SysMenuVO>>> 菜单分页列表
 */
export function getMenuPage(queryInfo: SysMenuQueryDTO) {
  return lcRequest.post<R<Page<SysMenuVO>>>({
    url: MODULE_MENU_URL + '/page',
    data: queryInfo
  })
}

/**
 * 查询菜单列表（树形，用于菜单管理树形展示）
 * @description 获取全部菜单并转为树形结构
 * @param queryInfo 查询条件
 * @returns Promise<R<SysMenuVO[]>> 菜单树列表
 */
export function getMenuList(queryInfo: Omit<SysMenuQueryDTO, 'current' | 'size'> = {}) {
  return lcRequest.post<R<Page<SysMenuVO>>>({
    url: MODULE_MENU_URL + '/page',
    data: { ...queryInfo, current: 1, size: 9999 }
  })
}

/**
 * 新增菜单
 * @description 用于新增系统菜单
 * @param menuData 菜单数据
 * @returns Promise<R<boolean>> 操作结果
 */
export function addMenu(menuData: CreateMenuDTO) {
  return lcRequest.post<R<boolean>>({
    url: MODULE_MENU_URL + '/add',
    data: menuData
  })
}

/**
 * 修改菜单
 * @description 用于修改系统菜单信息
 * @param menuData 菜单数据
 * @returns Promise<R<boolean>> 操作结果
 */
export function updateMenu(menuData: UpdateMenuDTO) {
  return lcRequest.post<R<boolean>>({
    url: MODULE_MENU_URL + '/edit',
    data: menuData
  })
}

/**
 * 删除菜单
 * @description 用于删除系统菜单
 * @param menuId 菜单ID
 * @returns Promise<R<boolean>> 操作结果
 */
export function deleteMenu(menuId: number) {
  return lcRequest.get<R<boolean>>({
    url: MODULE_MENU_URL + '/remove/' + menuId
  })
}

/**
 * 校验菜单名称唯一性
 * @description 用于校验菜单名称是否唯一
 * @param data 菜单名称数据
 * @returns Promise<R<boolean>> 校验结果
 */
export function checkMenuNameUnique(data: CheckMenuNameUniqueDTO) {
  return lcRequest.post<R<boolean>>({
    url: MODULE_MENU_URL + '/checkMenuNameUnique',
    data
  })
}

/**
 * 获取路由信息
 * @description 用于获取系统路由信息
 * @returns Promise<R<RouterVo[]>> 路由信息列表
 */
export function getRouters() {
  return lcRequest.get<R<RouterVo[]>>({
    url: MODULE_MENU_URL + '/getRouters',
    showLoading: false
  })
}

/**
 * 加载角色菜单列表树
 * @description 用于加载指定角色的菜单列表树
 * @param roleId 角色ID
 * @returns Promise<R<MenuTreeVO[]>> 角色菜单树数据
 */
export function getRoleMenuTreeData(roleId: number) {
  return lcRequest.get<R<MenuTreeVO[]>>({
    url: MODULE_MENU_URL + '/roleMenuTreeData/' + roleId
  })
}
